import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { operationalPollingOptions } from '../../api/statisticsQueries';
import useObservationActivity from '../../hooks/useObservationActivity';
import ObservationStatus from '../../ui/ObservationStatus';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Swords, Trophy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDmPersona } from '../../context/DmPersonaContext';
import UserDetailModal from '../../components/UserDetailModal/UserDetailModal';
import {
  RPS_CHOICE,
  RPS_STATUS,
  getRpsInbox,
  getRpsStats,
  postRpsChallenge,
  postRpsRematch,
  postRpsRespond,
} from '../../api/gamePlay';
import { getGameRpsHistory } from '../../api/gameRps';
import { getAccountMe, setAccountGameMode } from '../../api/userAccount';
import { t } from '../../utils/i18n';
import { isPersonaAuthError } from '../../utils/personaAuth';
import { usePersonaAuthRedirect } from '../../utils/usePersonaAuthRedirect';
import { useUsersByIds } from '../../hooks/useUsersByIds';
import UserChip from '../../components/UserChip';
import GameRpsTabs from './GameRpsTabs';

let lastAdminToken;
let lastPersonaToken;
let playSession = 0;
function gameSession(adminToken, personaToken) {
  if (lastAdminToken !== adminToken || lastPersonaToken !== personaToken) { lastAdminToken = adminToken; lastPersonaToken = personaToken; playSession += 1; }
  return playSession;
}
const CHOICES = [
  { value: RPS_CHOICE.ROCK, emoji: '✊', key: 'rock' },
  { value: RPS_CHOICE.PAPER, emoji: '✋', key: 'paper' },
  { value: RPS_CHOICE.SCISSORS, emoji: '✌️', key: 'scissors' },
];

function choiceMeta(value) {
  return CHOICES.find((c) => c.value === Number(value));
}

function mapPlayError(err) {
  const code = err?.code;
  if (code === -5501) return t('gameRps.play.errors.myGameOff');
  if (code === -5505) return t('gameRps.play.errors.oppGameOff');
  if (code === -5506) return t('gameRps.play.errors.self');
  if (code === -5502) return t('gameRps.play.errors.choice');
  if (code === -5503) return t('gameRps.play.errors.matchState');
  if (code === -5504) return t('gameRps.play.errors.notPlayer');
  return err?.message || t('gameRps.play.errors.generic');
}

function ChoicePicker({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {CHOICES.map((c) => {
        const active = Number(value) === c.value;
        return (
          <button
            key={c.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(c.value)}
            className={[
              'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 transition-all',
              active
                ? 'border-primary bg-primary/15 text-primary shadow-[0_0_0_3px_rgba(255,192,0,0.12)]'
                : 'border-dark-border bg-dark-bg/60 text-dark-text-secondary hover:border-primary/40 hover:text-dark-text',
              disabled ? 'opacity-40 cursor-not-allowed' : '',
            ].join(' ')}
          >
            <span className="text-2xl leading-none">{c.emoji}</span>
            <span className="text-[11px] font-semibold">
              {t(`gameRps.play.choice.${c.key}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StatusChip({ status }) {
  if (status === RPS_STATUS.PENDING) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full border border-amber-500/35 bg-amber-500/10 text-amber-200 text-[11px] font-medium">
        {t('gameRps.play.status.pending')}
      </span>
    );
  }
  if (status === RPS_STATUS.DRAW) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full border border-sky-500/35 bg-sky-500/10 text-sky-300 text-[11px] font-medium">
        {t('gameRps.play.status.draw')}
      </span>
    );
  }
  return (
    <span className="inline-flex px-2 py-0.5 rounded-full border border-emerald-500/35 bg-emerald-500/10 text-emerald-300 text-[11px] font-medium">
      {t('gameRps.play.status.completed')}
    </span>
  );
}

function resultKind(match, myId) {
  if (match.status === RPS_STATUS.DRAW) return 'draw';
  if (match.status !== RPS_STATUS.COMPLETED) return null;
  if (Number(match.winner_user_id) === Number(myId)) return 'win';
  return 'lose';
}

function ResultChip({ kind }) {
  if (kind === 'win') {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full border border-sky-400/50 bg-sky-500/15 text-sky-300 text-[11px] font-bold">
        {t('gameRps.play.result.win')}
      </span>
    );
  }
  if (kind === 'lose') {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full border border-red-500/45 bg-red-500/15 text-red-400 text-[11px] font-bold">
        {t('gameRps.play.result.lose')}
      </span>
    );
  }
  if (kind === 'draw') {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full border border-zinc-400/35 bg-zinc-400/10 text-zinc-300 text-[11px] font-bold">
        {t('gameRps.play.result.draw')}
      </span>
    );
  }
  return null;
}

function ThrowCard({ label, choice, highlight }) {
  const meta = choiceMeta(choice);
  const tone = {
    win: 'border-sky-400/45 bg-sky-500/10',
    lose: 'border-red-500/35 bg-red-500/10',
    opp: 'border-amber-400/35 bg-amber-500/10',
  }[highlight] || 'border-dark-border bg-dark-bg/70';
  return (
    <div className={`flex flex-col items-center gap-1 rounded-xl border px-3 py-3 ${tone}`}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-dark-text-muted">
        {label}
      </span>
      <span className="text-3xl leading-none">{meta?.emoji || '—'}</span>
      <span className="text-sm font-semibold text-dark-text">
        {meta ? t(`gameRps.play.choice.${meta.key}`) : '—'}
      </span>
    </div>
  );
}

export default function GameRpsPlay() {
  const { token: adminToken, logout } = useAuth();
  const { accessToken, userId, username } = useDmPersona();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { apiEnvironment } = useApiEnvironment();
  const observation = useObservationActivity();
  const session = gameSession(adminToken, accessToken);
  const [opponentId, setOpponentId] = useState('');
  const [challengeChoice, setChallengeChoice] = useState(RPS_CHOICE.ROCK);
  const [respondChoice, setRespondChoice] = useState({});
  const [outgoing, setOutgoing] = useState([]);
  const [flash, setFlash] = useState('');
  const [formError, setFormError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const hasPendingMatch = outgoing.some(match => match.status === RPS_STATUS.PENDING);

  const invalidatePlay = () => {
    queryClient.invalidateQueries({ queryKey: ['game-rps-play'] });
  };

  const inboxQuery = useQuery({
    queryKey: ['game-rps-play', 'inbox', userId, apiEnvironment, session],
    queryFn: () => getRpsInbox(accessToken, { limit: 30 }),
    enabled: query => !!adminToken && !!accessToken && observation.active && query.state.status !== 'error',
    ...operationalPollingOptions(4000),
  });

  const historyQuery = useQuery({
    queryKey: ['game-rps-play', 'history', userId, apiEnvironment, session],
    queryFn: () => getGameRpsHistory(adminToken, { userId, limit: 20 }),
    refetchInterval: query => observation.active && hasPendingMatch && query.state.status !== 'error' ? 8000 : false,
    enabled: !!adminToken && !!userId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const statsQuery = useQuery({
    queryKey: ['game-rps-play', 'stats', userId, apiEnvironment, session],
    queryFn: () => getRpsStats(accessToken, userId),
    refetchInterval: query => observation.active && hasPendingMatch && query.state.status !== 'error' ? 8000 : false,
    enabled: !!adminToken && !!accessToken && !!userId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const accountQuery = useQuery({
    queryKey: ['game-rps-play', 'account', userId, apiEnvironment, session],
    queryFn: () => getAccountMe(accessToken),
    enabled: !!adminToken && !!accessToken,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const playOn = Number(accountQuery.data?.game_mode) === 1;
  useEffect(() => {
    const resolved = new Map((historyQuery.data || []).filter(match => match.status !== RPS_STATUS.PENDING).map(match => [String(match.id), match]));
    if (!outgoing.some(match => match.status === RPS_STATUS.PENDING && resolved.has(String(match.id)))) return;
    setOutgoing(previous => previous.map(match => resolved.get(String(match.id)) || match));
    queryClient.invalidateQueries({ queryKey: ['game-rps-play', 'stats', userId, apiEnvironment, session] });
  }, [historyQuery.data, outgoing, queryClient, userId, apiEnvironment, session]);

  const refreshPlay = () => {
    observation.resume();
    return Promise.all([inboxQuery, historyQuery, statsQuery, accountQuery].map(query => query.refetch({ cancelRefetch: false })));
  };

  const challengeMut = useMutation({
    mutationFn: () =>
      postRpsChallenge(accessToken, {
        opponentUserId: opponentId,
        choice: challengeChoice,
      }),
    onSuccess: (match) => {
      setFormError('');
      setFlash(t('gameRps.play.challengeSent'));
      window.setTimeout(() => setFlash(''), 2200);
      if (match) setOutgoing((prev) => [match, ...prev.filter((m) => m.id !== match.id)]);
      invalidatePlay();
    },
    onError: (err) => {
      if (!isPersonaAuthError(err)) setFormError(mapPlayError(err));
    },
  });

  const respondMut = useMutation({
    mutationFn: ({ matchId, choice }) => postRpsRespond(accessToken, { matchId, choice }),
    onSuccess: () => {
      setFlash(t('gameRps.play.responded'));
      window.setTimeout(() => setFlash(''), 2200);
      invalidatePlay();
    },
    onError: (err) => {
      if (!isPersonaAuthError(err)) setFormError(mapPlayError(err));
    },
  });

  const rematchMut = useMutation({
    mutationFn: ({ matchId, choice }) => postRpsRematch(accessToken, { matchId, choice }),
    onSuccess: (match) => {
      setFlash(t('gameRps.play.rematchSent'));
      window.setTimeout(() => setFlash(''), 2200);
      if (match) setOutgoing((prev) => [match, ...prev.filter((m) => m.id !== match.id)]);
      invalidatePlay();
    },
    onError: (err) => {
      if (!isPersonaAuthError(err)) setFormError(mapPlayError(err));
    },
  });

  const playModeMut = useMutation({
    mutationFn: (on) => setAccountGameMode(accessToken, on),
    onSuccess: (data, on) => {
      queryClient.setQueryData(['game-rps-play', 'account', userId, apiEnvironment, session], data);
      setFormError('');
      setFlash(on ? t('gameRps.play.playToggledOn') : t('gameRps.play.playToggledOff'));
      window.setTimeout(() => setFlash(''), 2200);
    },
    onError: (err) => {
      if (!isPersonaAuthError(err)) {
        setFormError(err?.message || t('gameRps.play.playToggleError'));
      }
    },
  });

  usePersonaAuthRedirect(
    inboxQuery.error,
    statsQuery.error,
    accountQuery.error,
    challengeMut.error,
    respondMut.error,
    rematchMut.error,
    playModeMut.error,
  );

  const inbox = useMemo(() => inboxQuery.data || [], [inboxQuery.data]);
  const history = useMemo(() => historyQuery.data || [], [historyQuery.data]);
  const stats = statsQuery.data;
  const refreshing = inboxQuery.isFetching || historyQuery.isFetching;

  const waiting = useMemo(() => {
    const inboxIds = new Set(inbox.map((m) => m.id));
    return outgoing.filter(
      (m) => m.status === RPS_STATUS.PENDING && !inboxIds.has(m.id)
    );
  }, [outgoing, inbox]);

  const playUserIds = useMemo(() => {
    const ids = [userId];
    inbox.forEach((m) => ids.push(m.challenger_user_id, m.opponent_user_id));
    waiting.forEach((m) => ids.push(m.challenger_user_id, m.opponent_user_id));
    history.forEach((m) => ids.push(m.challenger_user_id, m.opponent_user_id));
    return ids;
  }, [userId, inbox, waiting, history]);
  const { usersById } = useUsersByIds(playUserIds, adminToken);
  const userOf = (id) => usersById[id] || usersById[Number(id)];
  const openUserDetail = (id) => {
    const n = Number(id);
    if (Number.isInteger(n) && n > 0) setSelectedUserId(n);
  };
  const closeUserDetail = () => {
    setSelectedUserId(null);
    queryClient.invalidateQueries({ queryKey: ['game-rps-play', 'account'] });
  };

  const onChallenge = (e) => {
    e.preventDefault();
    const n = Number(opponentId);
    if (!Number.isInteger(n) || n <= 0) {
      setFormError(t('gameRps.play.errors.opponent'));
      return;
    }
    if (Number(n) === Number(userId)) {
      setFormError(t('gameRps.play.errors.self'));
      return;
    }
    challengeMut.mutate();
  };

  return (
    <div className="px-4 py-6 sm:px-6 max-w-[920px] space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary border border-primary/25">
              <Swords size={18} strokeWidth={2.25} />
            </span>
            <h1 className="text-xl sm:text-2xl font-bold text-dark-text tracking-tight">
              {t('gameRps.play.title')}
            </h1>
          </div>
          <p className="text-sm text-dark-text-secondary pl-[46px]">
            {t('gameRps.play.description')}
          </p>
        </div>
        <button
          type="button"
          onClick={refreshPlay}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dark-border text-sm text-dark-text-secondary hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : undefined} />
          {t('gameRps.actions.refresh')}
        </button>
      </header>

      <ObservationStatus reason={observation.reason || (inboxQuery.isError ? 'failure' : null)} lastReadAt={inboxQuery.dataUpdatedAt} onResume={refreshPlay} disabled={!adminToken || !accessToken || refreshing} />
      <GameRpsTabs active="play" />

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-dark-text-muted">
              {t('gameRps.play.playingAs')}
            </p>
            <UserChip
              userId={userId}
              user={userOf(userId) || { username, user_id: userId }}
              size={36}
              onClick={openUserDetail}
              title={t('gameRps.play.openUserDetail')}
            />
          </div>
          {accountQuery.isFetched && (
            <div className="flex flex-wrap items-center gap-2">
              <span
                title={t('gameRps.play.playOnHint')}
                className={[
                  'inline-flex flex-col justify-center rounded-full border px-3 py-1',
                  playOn
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : 'border-slate-500/40 bg-slate-500/10',
                ].join(' ')}
              >
                <span
                  className={[
                    'text-xs font-semibold leading-none',
                    playOn ? 'text-emerald-200' : 'text-slate-300',
                  ].join(' ')}
                >
                  {playOn ? t('gameRps.play.playOn') : t('gameRps.play.playOff')}
                </span>
                <span className="mt-0.5 text-[10px] leading-none text-dark-text-muted">
                  {t('gameRps.play.gameModeValue', { value: playOn ? 1 : 0 })}
                </span>
              </span>
              <button
                type="button"
                title={t('gameRps.play.playOnHint')}
                disabled={playModeMut.isPending}
                onClick={() => playModeMut.mutate(!playOn)}
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors disabled:opacity-50',
                  playOn
                    ? 'border-dark-border text-dark-text-secondary hover:border-red-400/50 hover:text-red-300'
                    : 'border-amber-500/40 bg-amber-500/10 text-amber-200 hover:border-amber-400',
                ].join(' ')}
              >
                {playModeMut.isPending && <Loader2 size={12} className="animate-spin" />}
                {playOn ? t('gameRps.play.turnOff') : t('gameRps.play.turnOn')}
              </button>
            </div>
          )}
        </div>
        {stats && (
          <div className="flex items-center gap-3 text-xs text-dark-text-secondary">
            <span className="tabular-nums">
              {t('gameRps.play.statsLine', {
                wins: stats.wins ?? 0,
                losses: stats.losses ?? 0,
                draws: stats.draws ?? 0,
              })}
            </span>
            <span className="inline-flex items-center gap-1 text-primary font-semibold">
              <Trophy size={12} />
              {Math.round((stats.overall_win_rate || 0) * 100)}%
            </span>
          </div>
        )}
      </div>

      {formError && <p className="text-sm text-red-400">{formError}</p>}
      {flash && <p className="text-sm text-emerald-400">{flash}</p>}

      <form
        onSubmit={onChallenge}
        className="rounded-xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface/40 p-5 space-y-4"
      >
        <div>
          <h2 className="text-base font-semibold text-dark-text">
            {t('gameRps.play.challengeTitle')}
          </h2>
          <p className="text-sm text-dark-text-secondary mt-0.5">
            {t('gameRps.play.challengeHint')}
          </p>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-dark-text-secondary">
            {t('gameRps.play.opponentId')}
          </span>
          <input
            type="number"
            min={1}
            value={opponentId}
            onChange={(e) => {
              setOpponentId(e.target.value);
              setFormError('');
            }}
            placeholder="42"
            className="w-full sm:w-56 rounded-lg border border-dark-border bg-dark-bg text-dark-text px-3 py-2 text-sm focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none"
          />
        </label>
        <ChoicePicker
          value={challengeChoice}
          onChange={setChallengeChoice}
          disabled={challengeMut.isPending}
        />
        <button
          type="submit"
          disabled={challengeMut.isPending}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-black text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40"
        >
          {challengeMut.isPending ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              {t('gameRps.play.sending')}
            </>
          ) : (
            t('gameRps.play.sendChallenge')
          )}
        </button>
      </form>

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-dark-text">
          {t('gameRps.play.inboxTitle')}
          {inbox.length > 0 && (
            <span className="ml-2 text-xs font-medium text-primary">{inbox.length}</span>
          )}
        </h2>
        {inboxQuery.isLoading && (
          <p className="text-sm text-dark-text-secondary">{t('gameRps.loading')}</p>
        )}
        {!inboxQuery.isLoading && inbox.length === 0 && (
          <div className="rounded-xl border border-dashed border-dark-border bg-dark-card/40 px-5 py-8 text-center text-sm text-dark-text-secondary">
            {t('gameRps.play.inboxEmpty')}
          </div>
        )}
        {inbox.map((match) => (
          <div
            key={match.id}
            className="rounded-xl border border-dark-border bg-dark-card/70 p-4 space-y-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-dark-text-muted mb-1">
                  {t('gameRps.play.fromUserLabel')}
                </p>
                <UserChip userId={match.challenger_user_id} user={userOf(match.challenger_user_id)} onClick={openUserDetail} title={t('gameRps.play.openUserDetail')} />
              </div>
              <StatusChip status={match.status} />
            </div>
            <ChoicePicker
              value={respondChoice[match.id] ?? RPS_CHOICE.ROCK}
              onChange={(v) => setRespondChoice((prev) => ({ ...prev, [match.id]: v }))}
              disabled={respondMut.isPending}
            />
            <button
              type="button"
              disabled={respondMut.isPending}
              onClick={() =>
                respondMut.mutate({
                  matchId: match.id,
                  choice: respondChoice[match.id] ?? RPS_CHOICE.ROCK,
                })
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-black text-sm font-semibold hover:bg-primary/90 disabled:opacity-40"
            >
              {respondMut.isPending ? t('gameRps.play.sending') : t('gameRps.play.respond')}
            </button>
          </div>
        ))}
      </section>

      {waiting.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-dark-text">
            {t('gameRps.play.waitingTitle')}
          </h2>
          {waiting.map((match) => {
            const mine = choiceMeta(match.challenger_choice);
            return (
              <div
                key={match.id}
                className="rounded-xl border border-dark-border bg-dark-card/50 px-4 py-3 flex flex-wrap items-center justify-between gap-2"
              >
                <div className="flex flex-wrap items-center gap-3 min-w-0">
                  <UserChip userId={match.opponent_user_id} user={userOf(match.opponent_user_id)} onClick={openUserDetail} title={t('gameRps.play.openUserDetail')} />
                  {mine && (
                    <span className="text-sm text-dark-text-secondary">
                      {mine.emoji} {t(`gameRps.play.choice.${mine.key}`)}
                    </span>
                  )}
                </div>
                <StatusChip status={RPS_STATUS.PENDING} />
              </div>
            );
          })}
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-dark-text">
          {t('gameRps.play.historyTitle')}
        </h2>
        {historyQuery.isLoading && (
          <p className="text-sm text-dark-text-secondary">{t('gameRps.loading')}</p>
        )}
        {historyQuery.isError && !historyQuery.isLoading && (
          <p className="text-sm text-red-400">{mapPlayError(historyQuery.error)}</p>
        )}
        {!historyQuery.isLoading && !historyQuery.isError && history.length === 0 && (
          <div className="rounded-xl border border-dashed border-dark-border bg-dark-card/40 px-5 py-8 text-center text-sm text-dark-text-secondary">
            {t('gameRps.play.historyEmpty')}
          </div>
        )}
        {history.map((match) => {
          const oppId =
            Number(match.challenger_user_id) === Number(userId)
              ? match.opponent_user_id
              : match.challenger_user_id;
          const myChoice =
            Number(match.challenger_user_id) === Number(userId)
              ? match.challenger_choice
              : match.opponent_choice;
          const oppChoice =
            Number(match.challenger_user_id) === Number(userId)
              ? match.opponent_choice
              : match.challenger_choice;
          const kind = resultKind(match, userId);
          const iWon = kind === 'win';
          const showPoints = iWon && Number(match.point_awarded) === 1 && Number(match.point_amount) > 0;
          return (
            <div
              key={match.id}
              className="rounded-xl border border-dark-border bg-dark-card/70 p-4 space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex flex-wrap items-center gap-2 min-w-0">
                  <UserChip userId={oppId} user={userOf(oppId)} onClick={openUserDetail} title={t('gameRps.play.openUserDetail')} />
                  <ResultChip kind={kind} />
                  {showPoints ? (
                    <span className="text-xs font-semibold text-primary">
                      +{match.point_amount} {t('gameRps.play.points')}
                    </span>
                  ) : null}
                </div>
                <StatusChip status={match.status} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ThrowCard
                  label={t('gameRps.play.myThrow')}
                  choice={myChoice}
                  highlight={kind === 'win' || kind === 'lose' ? kind : undefined}
                />
                <ThrowCard
                  label={t('gameRps.play.oppThrow')}
                  choice={oppChoice}
                  highlight="opp"
                />
              </div>
              {match.status === RPS_STATUS.DRAW && (
                <div className="space-y-2">
                  <ChoicePicker
                    value={respondChoice[`r-${match.id}`] ?? RPS_CHOICE.ROCK}
                    onChange={(v) =>
                      setRespondChoice((prev) => ({ ...prev, [`r-${match.id}`]: v }))
                    }
                    disabled={rematchMut.isPending}
                  />
                  <button
                    type="button"
                    disabled={rematchMut.isPending}
                    onClick={() =>
                      rematchMut.mutate({
                        matchId: match.id,
                        choice: respondChoice[`r-${match.id}`] ?? RPS_CHOICE.ROCK,
                      })
                    }
                    className="px-4 py-2 rounded-lg border border-primary/40 text-primary text-sm font-semibold hover:bg-primary/10 disabled:opacity-40"
                  >
                    {t('gameRps.play.rematch')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {selectedUserId && adminToken && (
        <UserDetailModal
          userId={selectedUserId}
          token={adminToken}
          onClose={closeUserDetail}
          onUnauthorized={() => {
            logout();
            navigate('/login', { replace: true });
          }}
          onNavigateUserId={setSelectedUserId}
          initialUser={userOf(selectedUserId)}
        />
      )}
    </div>
  );
}
